import { CourseCardsProps } from "design-system/sites/online/components/course-card-tease/course-card-tease";
import { formatDate } from "design-system/utils/date-format";
import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { documentToString } from "frontend/contentful/lib/document-to-string";
import { CoursePageEntry } from "frontend/contentful/schema/pages";
import { TermEntry } from "frontend/contentful/schema/semantics";
import { useContentful } from "frontend/hooks/use-contentful";
import { CourseSession } from "frontend/types";
import { createMediaAssetProps } from "../../primitives/media-asset.props";

export const createCourseTeaseProps = createContentfulProps<
  CoursePageEntry,
  CourseCardsProps
>(({ entry, createEditAttributes }) => {
  const { getEntry, getEntryUrl, getEntries } = useContentful();
  const courseEntry = getEntry(entry);
  if (!courseEntry) return;
  // Limit time field to 26 characters, when over 26 , trim it and add ellipsis
  let time = entry?.fields.time;
  if (time && time.length > 26) {
    time = time.substring(0, 23).trim() + "...";
  }
  const upcomingSessions: Array<CourseSession> = [];
  const sortedCourseSessions = getEntries(courseEntry.fields.sessions)
    .filter((session) => {
      const type = session.fields.sessionType?.trim().toLowerCase();
      return (
        type === "on demand" ||
        Boolean(
          session.fields.startDate &&
            new Date(session.fields.startDate).getTime() > Date.now(),
        )
      );
    })
    .sort((a, b) => {
      if (!a.fields.startDate || !b.fields.startDate) return 0;
      return (
        new Date(a.fields.startDate).getTime() -
        new Date(b.fields.startDate).getTime()
      );
    });

  sortedCourseSessions.forEach((sessionEntry) => {
    const onDemand =
      sessionEntry.fields.format?.toLowerCase().trim() === "on demand";
    const isMultiCourse = courseEntry.fields.isMultiCourse ? true : false;
    let format = undefined;
    if (isMultiCourse) {
      format = "Multi-Course";
    } else if (onDemand) {
      format = "On-Demand";
    } else {
      format = "Cohort-Based";
    }

    // Only Push sessions that have not started yet or are on demand or are more than 6 months in the future
    if (!onDemand) {
      const now = new Date().getTime();
      const startDate = sessionEntry.fields.startDate
        ? new Date(sessionEntry.fields.startDate).getTime()
        : 0;
      const sixMonthsFromNow = now + 180 * 24 * 60 * 60 * 1000;
      const alreadyStarted = startDate < now;
      if (alreadyStarted || startDate > sixMonthsFromNow) {
        return;
      }
    }

    upcomingSessions.push({
      startDate: onDemand ? format : formatDate(sessionEntry.fields.startDate),
      deadline: onDemand ? undefined : formatDate(sessionEntry.fields.deadline),
      type: sessionEntry?.fields.type,
      time: time,
      fee: sessionEntry.fields.cost,
      sessionType: sessionEntry.fields.sessionType,
      format: format,
    });
  });

  const {
    title,
    description,
    isMultiCourse,
    multiCourseCardHighlights,
    courseCount,
    topics,
    multiCourseCardImage,
  } = courseEntry.fields;

  const category =
    topics
      ?.map((topicRef) => {
        const term = getEntry<TermEntry>(topicRef);
        return term?.fields.title || "";
      })
      .filter(Boolean) || [];

  const props: CourseCardsProps = {
    type: "Course",

    id: `coursePage-${courseEntry.sys.id}`,
    title: title || "",
    overline: category[0],
    description: documentToString(description),
    link: getEntryUrl(courseEntry),
    category,

    sessions: upcomingSessions,

    isExEd: false,
    isMultiCourse,
    multiCourseLength: courseCount
      ? (parseInt(courseCount, 10) as number)
      : undefined,
    multiCourseCardHighlights,
    thumbnail: createMediaAssetProps(multiCourseCardImage),

    editAttributes: {
      id: createEditAttributes({
        entry: courseEntry,
        fieldId: "abbreviation",
      }),
      title: createEditAttributes({ entry: courseEntry, fieldId: "title" }),
      description: createEditAttributes({
        entry: courseEntry,
        fieldId: "description",
      }),
      link: createEditAttributes({ entry: courseEntry, fieldId: "url" }),
    },
  };

  return props;
});
